/**
 * What state the Map tab is in — `docs/property-maps-v2.md` §5.1.
 *
 * Five states in a fixed order, and the order is the whole rule: **no
 * location** beats everything (there is nowhere to draw), then a measure-only
 * session if the URL asked for one, then the editor if there is a map, then
 * the Empty screen. The flag only decides whether what you get is editable.
 *
 * A hook rather than a `useEffect` in the tab so the state is one value with
 * one name, and so the phone card and the desktop editor read the same answer.
 */
import { useCallback, useEffect, useState } from 'react'
import { lastMapOf, mapTab, type MapTabPayload } from './api.js'

export type MapTabState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'no_location' }
  | { kind: 'measure' }
  | { kind: 'maps'; openId: string }
  | { kind: 'empty' }

export interface PropertyMapsResult {
  payload: MapTabPayload | null
  state: MapTabState
  /** Whether drawing is allowed: Edit on Properties **and** the feature on. */
  canDraw: boolean
  reload: () => void
}

export function usePropertyMaps({
  propertyId, canEdit, measuring,
}: {
  propertyId: string
  canEdit: boolean
  /** `?measure=1` — state 4, which exists without a map. */
  measuring: boolean
}): PropertyMapsResult {
  const [payload, setPayload] = useState<MapTabPayload | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(() => {
    let live = true
    mapTab(propertyId)
      .then((body) => { if (live) { setPayload(body); setError(null) } })
      .catch((cause: unknown) => {
        if (live) {
          setError(cause instanceof Error ? cause.message : 'The maps could not be loaded.')
        }
      })
    return () => { live = false }
  }, [propertyId])

  useEffect(() => reload(), [reload])

  const state = stateOf(payload, error, measuring, propertyId)
  /*
   * Drawing needs three things and says so once: the permission, the feature,
   * and a pin to draw on. §5.7's *View only* banner is what somebody sees when
   * the first two are missing; state 1 is what they see when the third is.
   */
  const canDraw = canEdit && (payload?.features.maps ?? false) && (payload?.hasLocation ?? false)

  return { payload, state, canDraw, reload }
}

function stateOf(
  payload: MapTabPayload | null, error: string | null,
  measuring: boolean, propertyId: string,
): MapTabState {
  if (error) return { kind: 'error', message: error }
  if (!payload) return { kind: 'loading' }
  if (!payload.hasLocation) return { kind: 'no_location' }

  /*
   * A measure session beats a map, because `?measure=1` is something somebody
   * pressed — but only when there is a measure session to have: with the
   * feature off the link is stale and the ordinary states apply.
   */
  if (measuring && payload.features.measurements) return { kind: 'measure' }

  /* An archived map stays readable from the switcher and is never what the
     tab opens on (§5.6). */
  const live = payload.maps.filter((map) => !map.archived)
  if (live.length === 0) return { kind: 'empty' }

  const remembered = lastMapOf(propertyId)
  const open = live.find((map) => map.id === remembered) ?? live[0]!
  return { kind: 'maps', openId: open.id }
}
